import React from 'react'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { IconPlus } from '@amzn/stencil-react-components/icons'
import { Col, Row } from '@amzn/stencil-react-components/layout'
import { ScreenReaderOnly } from '@amzn/stencil-react-components/screen-reader-only'

import { ItemEditorBasicInfo } from 'src/components/ItemEditorBasicInfo'
import { isDefaultLocale } from 'src/contexts/ModuleLocaleContext'
import { useItemEntity, useValidationErrorEntity } from 'src/hooks/useEntity'
import {
    MAX_RATING_SCALE,
    MIN_RATING_SCALE,
    RatingItemDTO,
    RatingScaleDTO,
} from 'src/models/dto/items/RatingItemDTO'
import { ItemEditorProps } from 'src/pages/module-builder/item-editors/ItemEditorContainer'
import { ItemEditorTextInput } from 'src/pages/module-builder/item-editors/ItemEditorInputs'
import {
    CellType,
    ItemEditorTable,
    ItemEditorTableColumns,
} from 'src/pages/module-builder/item-editors/ItemEditorTable'
import { ItemEditorValidationErrors } from 'src/pages/module-builder/item-editors/ItemEditorValidationErrors'
import { RatingHandler } from 'src/services/EntityServices/ItemUpdateHandlers/RatingHandler'

export const RatingItemEditor = ({
    itemId,
    locale,
    workflowEntityId,
    showValidationError,
    editDisabled,
}: ItemEditorProps) => {
    const { entity: itemDTO } = useItemEntity<RatingItemDTO>({
        entityId: itemId,
        workflowEntityId,
    })

    const { entity: validationErrorEntity } = useValidationErrorEntity(itemId)

    const scaleTableColumns = [
        {
            header: 'Define rating scale',
            headerLabel: 'Define rating scale',
            width: '80%',
            cellType: CellType.TextInput,
            placeHolder: 'Rating scale',
            disabled: editDisabled,
            accessor: (data) => data.nameI18N[locale],
            setValue: (nextValue: string, index: number) =>
                RatingHandler.updateScaleName(itemDTO.id, index, locale, nextValue),
        },
        {
            header: 'Score',
            headerLabel: 'Score',
            width: '15%',
            disabled: !isDefaultLocale(locale) || editDisabled,
            cellType: CellType.NumberInput,
            placeHolder: '0.0',
            accessor: (data) => data.score,
            setValue: (nextValue: number, index: number) =>
                RatingHandler.updateScaleScore(itemDTO.id, index, nextValue),
        },
        {
            header: <ScreenReaderOnly>Delete action</ScreenReaderOnly>,
            headerLabel: 'Delete',
            width: '5%',
            disabled:
                !isDefaultLocale(locale) ||
                itemDTO.scales?.length <= MIN_RATING_SCALE ||
                editDisabled,
            cellType: CellType.DeleteButton,
            setValue: (nextValue: never, index: number) =>
                RatingHandler.deleteScale(itemDTO.id, index),
        },
    ] as ItemEditorTableColumns<RatingScaleDTO>

    return (
        <>
            <ItemEditorValidationErrors {...{ itemId, showValidationError }} />
            <Col gridGap={'S300'}>
                <ItemEditorBasicInfo
                    handler={RatingHandler}
                    itemId={itemId}
                    workflowEntityId={workflowEntityId}
                    locale={locale}
                    editDisabled={editDisabled}
                    showValidationError={showValidationError}
                />
                <Col width={'100%'}>
                    <ItemEditorTextInput
                        itemId={itemDTO.id}
                        dataTestId={'rating-question-input'}
                        inputId='rating-questions'
                        labelText='Stimulus'
                        placeholder='Enter a stimulus question'
                        disabled={editDisabled}
                        value={itemDTO.questionI18N[locale] || ''}
                        setValue={(nextValue: string) =>
                            RatingHandler.updateQuestion(itemDTO.id, locale, nextValue)
                        }
                        validationErrorMessage={(
                            validationErrorEntity.validationErrors.questionI18N ?? []
                        ).join(', ')}
                        showError={showValidationError}
                    />
                </Col>
                <Col width={'100%'}>
                    <ItemEditorTable
                        itemId={itemDTO.id}
                        columns={scaleTableColumns}
                        data={itemDTO.scales}
                        dataTestId='rating-scale-table'
                        validationErrorMessage={(
                            validationErrorEntity.validationErrors.scales ?? []
                        ).join(', ')}
                        showError={showValidationError}
                    />
                    <Row justifyContent={'flex-end'}>
                        <Button
                            dataTestId={'rating-scale-table-add-row-button'}
                            icon={<IconPlus aria-hidden={true} />}
                            variant={ButtonVariant.Tertiary}
                            disabled={!isDefaultLocale(locale)}
                            aria-disabled={
                                itemDTO.scales.length >= MAX_RATING_SCALE || editDisabled
                            } //Using aria-disabled make it to be still focusable after disable it for the accessibility.
                            onClick={() => RatingHandler.addScale(itemDTO.id)}
                        >
                            Add new row
                        </Button>
                    </Row>
                </Col>
            </Col>
        </>
    )
}
